import React, { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  MarkerType,
} from 'reactflow';
import { WorkflowNodeTemplate, WorkflowEdgeTemplate, NodeType } from '../../types';

interface WorkflowMiniPreviewProps {
  nodeTemplates: WorkflowNodeTemplate[];
  edgeTemplates: WorkflowEdgeTemplate[];
  width?: number;
  height?: number;
  className?: string;
}

const MiniPreviewNode: React.FC<{ data: any; type: string }> = ({ data, type }) => {
  const getNodeStyle = () => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 'bold',
      color: 'white',
      textAlign: 'center' as const,
      minWidth: '60px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid',
    };

    switch (type.toUpperCase()) {
      case NodeType.START:
        return { ...baseStyle, backgroundColor: '#10B981', borderColor: '#059669' };
      case NodeType.END:
        return { ...baseStyle, backgroundColor: '#EF4444', borderColor: '#DC2626' };
      case NodeType.ACTIVITY:
      case NodeType.POOL_ACTIVITY:
        return { ...baseStyle, backgroundColor: '#EC4899', borderColor: '#DB2777' };
      case 'TASK':
        return { ...baseStyle, backgroundColor: '#3B82F6', borderColor: '#2563EB' };
      case 'APPROVAL':
        return { ...baseStyle, backgroundColor: '#F59E0B', borderColor: '#D97706' };
      case 'DECISION':
        return { ...baseStyle, backgroundColor: '#8B5CF6', borderColor: '#7C3AED' };
      case 'NOTIFICATION':
        return { ...baseStyle, backgroundColor: '#06B6D4', borderColor: '#0891B2' };
      default:
        return { ...baseStyle, backgroundColor: '#6B7280', borderColor: '#4B5563' };
    }
  };

  return (
    <div style={getNodeStyle()}>
      {data.label || type}
    </div>
  );
};

const miniNodeTypes = {
  start: MiniPreviewNode,
  end: MiniPreviewNode,
  task: MiniPreviewNode,
  approval: MiniPreviewNode,
  decision: MiniPreviewNode,
  notification: MiniPreviewNode,
  activity: MiniPreviewNode,
  poolActivity: MiniPreviewNode,
  pool_activity: MiniPreviewNode,
};

const WorkflowMiniPreviewComponent: React.FC<WorkflowMiniPreviewProps> = ({
  nodeTemplates,
  edgeTemplates,
  width = 200,
  height = 150,
  className = '',
}) => {
  // Convert node templates to React Flow nodes
  const nodes = useMemo((): Node[] => {
    return nodeTemplates.map((nodeTemplate) => {
      let nodeData: any = {};

      try {
        nodeData = typeof nodeTemplate.nodeData === 'string'
          ? JSON.parse(nodeTemplate.nodeData)
          : nodeTemplate.nodeData;
      } catch (error) {
      }

      return {
        id: nodeTemplate.nodeId,
        type: nodeTemplate.nodeType.toLowerCase(),
        position: {
          x: nodeTemplate.positionX || 0,
          y: nodeTemplate.positionY || 0,
        },
        data: {
          label: nodeData.label || nodeTemplate.nodeType,
          ...nodeData,
        },
        draggable: false,
        selectable: false,
        focusable: false,
      };
    });
  }, [nodeTemplates]);

  // Convert edge templates to React Flow edges
  const edges = useMemo((): Edge[] => {
    return edgeTemplates.map((edgeTemplate) => {
      let edgeData: any = {};

      try {
        edgeData = edgeTemplate.edgeData
          ? (typeof edgeTemplate.edgeData === 'string'
            ? JSON.parse(edgeTemplate.edgeData)
            : edgeTemplate.edgeData)
          : {};
      } catch (error) {
      }

      return {
        id: edgeTemplate.edgeId,
        source: edgeTemplate.sourceNodeId,
        target: edgeTemplate.targetNodeId,
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: '#3B82F6',
          strokeWidth: 2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#3B82F6',
        },
        data: edgeData,
        focusable: false,
        selectable: false,
      };
    });
  }, [edgeTemplates]);

  // Calculate bounds to fit all nodes
  const fitViewOptions = useMemo(() => {
    if (nodes.length === 0) return { padding: 0.1 };

    return {
      padding: 0.2,
      includeHiddenNodes: false,
      minZoom: 0.1,
      maxZoom: 1,
    };
  }, [nodes]);

  const onInit = useCallback((reactFlowInstance: any) => {
    // Auto-fit the view when component initializes
    setTimeout(() => {
      reactFlowInstance.fitView(fitViewOptions);
    }, 100);
  }, [fitViewOptions]);

  if (nodes.length === 0) {
    return (
      <div
        className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`}
        style={{ width, height }}
      >
        <div className="text-center text-gray-500">
          <div className="text-xs">No Preview</div>
          <div className="text-xs">Available</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}
      style={{ width, height }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={miniNodeTypes}
        onInit={onInit}
        fitView
        fitViewOptions={fitViewOptions}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        minZoom={0.1}
        maxZoom={1}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#e5e7eb"
        />
      </ReactFlow>
    </div>
  );
};

export const WorkflowMiniPreview: React.FC<WorkflowMiniPreviewProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowMiniPreviewComponent {...props} />
    </ReactFlowProvider>
  );
};